import { Skeleton, Td, Tr } from '@chakra-ui/react'

/**
 * Quantas linhas de skeleton uma tabela mostra enquanto busca.
 *
 * Cinco, que é o que três das quatro cópias do idioma já usavam. Não é o `pageSize` da
 * listagem: 25 linhas cinzentas são um paredão, e o skeleton é sinal de que vêm linhas, não
 * previsão de quantas.
 */
export const TABLE_SKELETON_ROWS = 5

/**
 * Altura da barra dentro da célula.
 *
 * `14px` é o tamanho do texto do corpo da tabela definido no `theme.ts` — a barra substitui a
 * *linha de texto* da célula, e é essa a medida que ela imita. As quatro cópias do idioma
 * estavam empatadas em `16px` e `14px`; `14px` é a que tem razão de ser.
 *
 * A linha de skeleton não persegue os 49px da linha de dados de propósito: o número de linhas
 * também muda quando os dados chegam (5 viram até 100), então o salto de layout é inevitável e
 * igualar a altura não o evitaria.
 */
export const TABLE_SKELETON_LINE_HEIGHT = '14px'

export interface ITableSkeletonRowsProps {
  /** Número de colunas da tabela — o mesmo número que o `colSpan` do `TableEmptyRow` recebe. */
  cols: number
  /** Quantas linhas mostrar. Só mexa onde a tabela é curta por natureza. */
  rows?: number
}

/**
 * Estado de carregamento do corpo de uma tabela do painel — a irmã do `<TableEmptyRow>`.
 *
 * Vai no mesmo lugar, no `<Tbody>`, e resolve o mesmo tipo de problema: a página declara a
 * forma da tabela, o componente escreve a aparência. O idioma estava copiado quase igual em
 * quatro páginas (`NotificationsPage`, `SupportPage`, `MachinesPage`, `EventOrdersPage`), com
 * duas alturas de barra entre elas, e outras oito listagens punham um `<Spinner>` num
 * `<Td colSpan>` — um ponto girando onde vão aparecer linhas.
 *
 * O corpo de uma listagem é um caminho de três saídas, não de duas:
 *
 * ```tsx
 * <Tbody>
 *   {loading ? (
 *     <TableSkeletonRows cols={4} />
 *   ) : sectors.length === 0 ? (
 *     <TableEmptyRow colSpan={4} entity="setor" />
 *   ) : (
 *     sectors.map((sector) => ...)
 *   )}
 * </Tbody>
 * ```
 *
 * Sem o primeiro ramo a listagem afirma estar vazia enquanto ainda está buscando: 31 páginas
 * mostravam `Nenhum <registro> encontrado` durante o fetch. Isso ficou pior — não melhor — quando
 * o `TableEmptyRow` deu a essa linha uma frase específica e confiante.
 */
export default function TableSkeletonRows({ cols, rows = TABLE_SKELETON_ROWS }: ITableSkeletonRowsProps) {
  return (
    <>
      {Array.from({ length: rows }).map((_, row) => (
        <Tr key={row}>
          {Array.from({ length: cols }).map((__, col) => (
            <Td key={col}>
              <Skeleton height={TABLE_SKELETON_LINE_HEIGHT} />
            </Td>
          ))}
        </Tr>
      ))}
    </>
  )
}
